/**
 * F: Ledger integrity - WiP vs Schedule of Work cross-check.
 * Flags under-entered instalment schedules, missing/orphan projects
 * and duplicate FIIDs before they distort the revenue numbers.
 */
import { ExternalLink } from "lucide-react";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { useCfoData, TabState, KpiStrip, Card, SourceBadge, InfoTip } from "./cfoShared";
import QboReconciliationPanel from "./QboReconciliationPanel";

function Row({ children, testid }) {
  return <li data-testid={testid} className="py-2 flex items-center gap-3">{children}</li>;
}

export default function IntegrityTab() {
  const { data, loading, error } = useCfoData("cfo/integrity");
  if (loading || error) return <TabState loading={loading} error={error} testid="cfo-integrity" />;

  return (
    <div className="flex flex-col gap-4" data-testid="cfo-integrity">
      <KpiStrip cards={[
        { label: "Projects cross-checked", value: data.checked_projects.toLocaleString(), tone: "text-slate-900", testid: "integrity-kpi-checked", info: "int_checked" },
        { label: "Value mismatches", value: data.mismatch_count, tone: data.mismatch_count ? "text-amber-700" : "text-teal-700", testid: "integrity-kpi-mismatches", info: "int_mismatch", sub: "WiP total vs SoW turnover, >5% or £500" },
        { label: "Won but no instalments", value: data.missing_wip_count, tone: data.missing_wip_count ? "text-rose-700" : "text-teal-700", testid: "integrity-kpi-missing", info: "int_missing", sub: "post-commission SoW rows with no WiP rows" },
        { label: "WiP-only projects", value: data.orphan_wip_count, tone: "text-stone-500", testid: "integrity-kpi-orphans", info: "int_orphans", sub: "CIDs not found on the SoW" },
      ]} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="integrity-mismatches">
          <div className="flex items-center justify-between mb-1">
            <PanelTitle>Value mismatches · instalments don't add up to the project <InfoTip id="int_mismatch" /></PanelTitle>
            <SourceBadge kind="integrity" />
          </div>
          {data.mismatches.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">Every checked project reconciles within tolerance.</p>
          ) : (
            <ul className="divide-y divide-stone-50 max-h-96 overflow-y-auto pr-1">
              {data.mismatches.map((m) => (
                <Row key={m.cid} testid={`integrity-mismatch-${m.cid}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {m.client} <span className="text-stone-400 font-normal">· {m.project_name}</span>
                      {m.cognito_admin && (
                        <a href={m.cognito_admin} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex items-center gap-0.5 font-mono text-[10px] text-stone-400 hover:text-[#1B4F8A]">
                          #{m.cid}<ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </p>
                    <p className="text-[10px] font-mono text-stone-400">
                      instalments {fmtGbpShort(m.wip_total)} vs project {fmtGbpShort(m.sow_turnover)} · {m.status}
                    </p>
                  </div>
                  <span className={`text-xs font-mono tabular-nums shrink-0 ${m.diff < 0 ? "text-rose-700" : "text-amber-700"}`}>
                    {m.diff > 0 ? "+" : ""}{fmtGbpShort(m.diff)}
                  </span>
                </Row>
              ))}
            </ul>
          )}
        </Card>

        <Card testid="integrity-missing">
          <PanelTitle>Won on the SoW but no instalment rows <InfoTip id="int_missing" /></PanelTitle>
          {data.missing_wip.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">Every post-commission project has instalments.</p>
          ) : (
            <ul className="divide-y divide-stone-50 max-h-96 overflow-y-auto pr-1">
              {data.missing_wip.map((m) => (
                <Row key={m.cid} testid={`integrity-missing-${m.cid}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-800 truncate">
                      {m.client} <span className="text-stone-400 font-normal">· {m.project_name}</span>
                      {m.cognito_admin && (
                        <a href={m.cognito_admin} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex items-center gap-0.5 font-mono text-[10px] text-stone-400 hover:text-[#1B4F8A]">
                          #{m.cid}<ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                    </p>
                    <p className="text-[10px] font-mono text-stone-400">{m.status}</p>
                  </div>
                  <span className="text-xs font-mono tabular-nums text-slate-700 shrink-0">{fmtGbpShort(m.sow_turnover)}</span>
                </Row>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="integrity-duplicates">
          <PanelTitle>Duplicate FIIDs with value <InfoTip id="int_dupes" /></PanelTitle>
          {data.duplicate_fiids.length === 0 ? (
            <p className="text-xs text-stone-400 py-2">No valued duplicates.</p>
          ) : (
            <ul className="divide-y divide-stone-50">
              {data.duplicate_fiids.map((d) => (
                <Row key={d.fiid} testid={`integrity-dup-${d.fiid}`}>
                  <span className="text-xs font-mono text-slate-800">{d.fiid}</span>
                  <span className="text-xs text-stone-500 flex-1 truncate">{d.client}</span>
                  <span className="text-xs font-mono tabular-nums text-amber-700">{d.rows} rows</span>
                </Row>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-stone-400 mt-2">
            Both rows are currently counted (matches your accounting export). Fix in SharePoint if one is spurious.
          </p>
        </Card>
        <Card testid="integrity-orphans">
          <PanelTitle>WiP-only projects · top by value <InfoTip id="int_orphans" /></PanelTitle>
          <ul className="divide-y divide-stone-50 max-h-72 overflow-y-auto pr-1">
            {data.orphan_wip.sort((a, b) => b.wip_total - a.wip_total).slice(0, 15).map((o) => (
              <Row key={o.cid} testid={`integrity-orphan-${o.cid}`}>
                <span className="text-xs font-mono text-stone-400">#{o.cid}</span>
                <span className="text-xs text-slate-800 flex-1 truncate">{o.client}</span>
                <span className="text-xs font-mono tabular-nums text-stone-600">{fmtGbpShort(o.wip_total)}</span>
              </Row>
            ))}
          </ul>
          <p className="text-[10px] text-stone-400 mt-2">
            Mostly pre-SoW-era projects - informational, they still count in revenue.
          </p>
        </Card>
      </div>
      <QboReconciliationPanel />
    </div>
  );
}
